import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { iFlowVariable } from './api/flowInterfaces';
import { ConfirmModal } from '@/common/alerts';
import CreateFlowReportCommand from '@/common/api/commands/Reporting/CreateFlowReportCommand';
import { useScriptDesignStore } from '@/stores/scriptDesign';
import { useAuthenticationStore } from '@/stores/authentication';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';

export const useFlowDataStore = defineStore('flowData', () => {
    const flowId = ref('');
    const flowRunId = ref('');
    const flowVariables = ref<iFlowVariable[]>([]);
    const computedVariables = ref<any>();
    const lastChange = ref({name: '', time: 0})
    const flowStartDate = ref(new Date())
    const flowEndDate = ref(new Date())
    const router = useRouter();
    const isEndFlowTriggered = ref(false);
    const abandonedReason = ref<any>(null);
    const additionalReason = ref<any>(null);
    const abandonedFormName = ref<any>(null);
    const hasSaved = ref<Boolean>(false);
    const authStore = useAuthenticationStore();

    function setFlowAbandonedTrigger(value: boolean) {
        isEndFlowTriggered.value = value;
    }

    function setFlowAbandonedState(reason: string, addReason: string) {
        abandonedReason.value = reason
        additionalReason.value = addReason
    }

    function setAbandonedFormName(title: string) {
        abandonedFormName.value = title;
    }

    function startNewRun(flowData: any) {
        flowStartDate.value = new Date();
        flowId.value = flowData.start;
        flowRunId.value = crypto.randomUUID();
        flowVariables.value = flowData.state.map((variable) => ({name: variable.name, type: variable.type, value: null})) ?? [];
        computedVariables.value = flowData.computed ?? [];
        hasSaved.value = false

        lastChange.value = {name: '', time: 0};
    }

    function getFlowVariable(variableName : string, forReplace: boolean = false, computed: boolean = false, isExitCondition: boolean = false): any {
        if(variableName == undefined || variableName == null) return ""
        if(flowVariables.value == null || flowVariables.value.length == 0) 
        {
            return ""
        }

        const variable = flowVariables.value.find((item) => item.name === variableName.toLowerCase());

        if (variable != null && variable != undefined && variable.value != null) 
        {
            switch(variable.type) {
                case "date":
                    if(isExitCondition || (forReplace && computed))
                    {
                        return `new Date('${variable.value}')`
                    }
                    else if(forReplace) {
                        return DateTime.fromISO(`${variable.value}`).toLocaleString(DateTime.DATE_SHORT);
                    }

                    return variable.value
                case "string":
                    if(forReplace)
                    {
                        return `'${variable.value}'`
                    }
                    return variable.value.toString()
                default:
                    return variable.value
            }
        }
    
        return null;
    }

    function getComputedVariableValue(variableName : string) {
        if (computedVariables.value == null
            || computedVariables.value.length == 0
            || computedVariables.value.filter(c => c.name?.toLowerCase() == variableName?.toLowerCase()).length == 0) 
        {
            return `{{computed.${variableName}}}`
        }

        const computeds = computedVariables.value.filter(c => c.name?.toLowerCase() == variableName?.toLowerCase())
        const computed = computeds[0]

        return computed.value.includes('{')? resolveInputVariablePlaceholders(computed.value, true) : computed.value
    }

    function evaluateExpression(expression: string): string {
        try {
            return new Function(`"use strict"; return (${expression});`)();
        } catch (err) {
            return expression;
        }
    }

    function getSystemVariableValue(variableName : string){
        switch (variableName) {
            case "user":
                return authStore.user?.displayName;
            case "currentDate":
                return DateTime.now().toLocaleString();
            case "currentTime":
                return DateTime.now().toLocaleString(DateTime.TIME_SIMPLE);
            default:
                return `{{system.${variableName}}}`;
        }
    }

    function setFlowVariable(variableName : string, newValue : any) {
        if(newValue == '\'null\'') newValue = null
        let variable = flowVariables.value.find((item) => item.name === variableName.toLowerCase());
        if (variable == null || variable == undefined)
        {
            flowVariables.value.push({ name: variableName.toLowerCase(), value : newValue });
            lastChange.value = {name: variableName.toLowerCase(), time: Date.now()}
            return true;
        }
        variable.value = newValue;
        
        lastChange.value = {name: variableName.toLowerCase(), time: Date.now()}
        return true;
    }

    function getAllFlowVariableNames() {
        const varNames : string[] = new Array();
        for (let i in flowVariables.value) {
            varNames.push(flowVariables.value[i].name)
        }

        return varNames;
    }


    function getMergedExpression(expression: any, isExitCondition: boolean = false, shouldEvaluate: boolean = false): string {

        if(expression == null || expression.length == 0) return "";
        
        if (typeof expression !== "string" || expression == 'true' || expression == 'false') {
            return expression;
        }

        if (/\{\{computed\..*?\}\}/.test(expression)) {
            expression = resolveComputedVariablePlaceholders(expression);
        }

        if (/\{\{variable\..*?\}\}/.test(expression)) {
            expression = resolveInputVariablePlaceholders(expression, false, isExitCondition);
        }

        if (/\{\{system\..*?\}\}/.test(expression)) {
            expression = resolveSystemVariablePlaceholders(expression);
        }

        if(shouldEvaluate){
            return evaluateExpression(expression);
        }

        return expression;
    }

    function resolveComputedVariablePlaceholders(
        expression: string,
        depth: number = 0,
        maxDepth: number = 20): string {

        if (depth > maxDepth) {
          throw new Error("Maximum recursion depth exceeded. Check for circular references.");
        }

        if (computedVariables.value == null || computedVariables.value.length == 0) return expression;
      
        const computedVariablePlaceholderRegex = /(?<=\{\{computed\.)(.*?)(?=\}\})/gm;
        const matches = expression.match(computedVariablePlaceholderRegex);
        const uniqueMatches = [...new Set(matches)];
      
        if (uniqueMatches) {
            uniqueMatches.forEach((computedVariableName) => {
            let computedValue = getComputedVariableValue(computedVariableName);

            if (computedValue && /\{\{computed\..*?\}\}/.test(computedValue)) {
                computedValue = resolveComputedVariablePlaceholders(computedValue, depth + 1, maxDepth);
            } else {
                computedValue = evaluateExpression(computedValue);
            }
      
            expression = expression.replaceAll(`{{computed.${computedVariableName}}}`, computedValue);
          });
        }

        const placeHolderRegex = /\{\{.*?\}\}/;
        const htmlRegex = /<\s*\w+.*?>/;

        if (!placeHolderRegex.test(expression) && !htmlRegex.test(expression)) {
            return evaluateExpression(expression);
        }
      
        return expression;
    }

    function resolveInputVariablePlaceholders(expression: string, computed: boolean, isExitCondition: boolean = false) {
        const inputVariablePlaceholderRegex = /(?<=\{\{variable\.)(.*?)(?=\}\})/gm;
        const matches = expression.match(inputVariablePlaceholderRegex);
        const uniqueMatches = [...new Set(matches)];

        const isHtml = /<\s*\w+.*?>/.test(expression);

        if (uniqueMatches) {
            uniqueMatches.forEach((inputVariableName) => {
                const flowVariable = getFlowVariable(inputVariableName, !isHtml, computed, isExitCondition)
                expression = expression.replaceAll("{{variable." + inputVariableName + "}}", flowVariable);
            });

            if(!computed && !isHtml){
                return evaluateExpression(expression);
            }
        }

        return expression;
    }

    function resolveSystemVariablePlaceholders(expression: string) {
        const systemVariablePlaceholderRegex = /(?<=\{\{system\.)(.*?)(?=\}\})/gm;
        const matches = expression.match(systemVariablePlaceholderRegex);

        if (matches) {
            matches.forEach((systemVariableName) => {
                expression = expression.replaceAll("{{system." + systemVariableName + "}}", getSystemVariableValue(systemVariableName));
            });
        }

        return expression;
    }

    function RunActions(context: any, actions: any) {
        actions.forEach((action: any) => {
            if(eval(getMergedExpression(action.condition)))
            {
                action.trueActions.forEach((trueAction: any) => {
                    runAction(context, trueAction)
                })
            }
            else {
                action.falseActions.forEach((falseAction: any) => {
                    runAction(context, falseAction)
                })
            }
        })
    }

    function runAction(context: any, action: any) {
        switch(action.type) {
          case "alert":
            ConfirmModal(context, action.title, action.body);
            break;
          case "state":
          case "onClick":
          default: 
            eval(getMergedExpression(action.action))
        }
    }

    function resetStore() {
        
        flowStartDate.value = new Date();
        flowId.value = "";
        flowRunId.value = "";
        flowVariables.value = [];
        computedVariables.value = [];
        
        lastChange.value = {name: '', time: 0};
    }

    function saveFlow(redirect: Boolean) {
        if(hasSaved.value) return
        try {
            flowEndDate.value = new Date();

            const designStore = useScriptDesignStore();
            
            const command = new CreateFlowReportCommand();
            command.campaignId = designStore.campaignId;
            command.campaignName = designStore.campaignName;
            command.flowId = designStore.scriptId;
            command.flowName = designStore.scriptName;
            command.startDateTime = flowStartDate.value;
            command.endDateTime = flowEndDate.value;
            command.abandoned = isEndFlowTriggered.value; 
            command.abandonedReason = abandonedReason.value;
            command.additionalReason = additionalReason.value;
            command.abandonedFormName = abandonedFormName.value;

            command.Execute().then((result) => {
                if(redirect) {
                    router.push({ name: 'ViewConversations' });        
                }
            })

            isEndFlowTriggered.value = false;
            abandonedReason.value = null;
            additionalReason.value = null;
            hasSaved.value = true;
            
        } catch (e) {
        }
    }

    return {flowRunId, flowVariables, computedVariables, lastChange, flowId, isEndFlowTriggered, abandonedReason, additionalReason, saveFlow, startNewRun, resetStore, runAction, RunActions, getFlowVariable, setFlowVariable, getAllFlowVariableNames, getMergedExpression, setFlowAbandonedTrigger, setFlowAbandonedState, setAbandonedFormName}
})